import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  TrendingUp,
  Home,
  School,
  AttachMoney,
  Assessment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function Dashboard() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userData, setUserData] = useState(null);
  const username = localStorage.getItem('username');

  const handleLogout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    navigate('/login');
  }, [navigate]);

  const fetchUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  }, [handleLogout]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const features = [
    {
      title: 'Retirement Planning',
      description: 'Plan your retirement savings and track your progress',
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#00b9ad',
      action: () => console.log('Retirement Planning'),
    },
    {
      title: 'Home Buying',
      description: 'Calculate affordability and plan your home purchase',
      icon: <Home sx={{ fontSize: 40 }} />,
      color: '#007a9a',
      action: () => console.log('Home Buying'),
    },
    {
      title: 'Education Savings',
      description: 'Plan for education expenses and 529 savings',
      icon: <School sx={{ fontSize: 40 }} />,
      color: '#4fa9bb',
      action: () => console.log('Education Savings'),
    },
    {
      title: 'Financial Goals',
      description: 'Set and track your financial goals',
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      color: '#00b9ad',
      action: () => console.log('Financial Goals'),
    },
    {
      title: 'Risk Assessment',
      description: 'Assess your risk tolerance and get portfolio recommendations',
      icon: <Assessment sx={{ fontSize: 40 }} />,
      color: '#007a9a',
      action: () => console.log('Risk Assessment'),
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Box
            component="img"
            sx={{
              height: 40,
              width: 40,
              mr: 2,
            }}
            alt="FiStone Logo"
            src="/fistone-logo.png"
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            FiStone Financial Planning
          </Typography>
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                {username ? username[0].toUpperCase() : <AccountCircle />}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography variant="body2">{username}</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Welcome back{userData?.firstName ? `, ${userData.firstName}` : ''}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose a financial planning tool to get started
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={feature.action}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                      color: feature.color,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography gutterBottom variant="h6" component="div">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ mt: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid>
              <Button variant="outlined" color="primary">
                View Goals
              </Button>
            </Grid>
            <Grid>
              <Button variant="outlined" color="primary">
                Add Associated Person
              </Button>
            </Grid>
            <Grid>
              <Button variant="outlined" color="primary">
                Financial Calculators
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default Dashboard;